/* GCS Community als Instagram-Post-Slider (Produktseite) – nur von sections/gcs-product-community.liquid geladen (defer).
   <gcs-community-slider>: gleiches Muster wie <gcs-reviews-slider> (assets/gcs-reviews.js), ohne „Mehr lesen“.
   Umfasst Kopf (Buttons rechts) und Track (Fortschrittsbalken darunter). Buttons und Balken sind bis zum Start unsichtbar,
   belegen aber schon ihren Platz (kein Layout-Sprung); ist nichts zu scrollen, werden sie ausgeblendet ([hidden]).
   Scroll-Snap-Track (Wischen bleibt nativ), Buttons vor/zurück auf allen Breiten (Schrittweite = ganz sichtbare Karten),
   Fortschrittsbalken, Pfeiltasten links/rechts, solange der Fokus im Slider ist, Ansage „Post 2 von 6“ für Screenreader.
   Am Anfang/Ende: aria-disabled statt disabled, damit der Tastaturfokus auf dem Button bleibt.
   Ohne JS: Track per Touch/Trackpad scrollbar, Buttons unsichtbar. */
(() => {
  if (customElements.get('gcs-community-slider')) return;

  const EDGE = 2;
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class GcsCommunitySlider extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.track = this.querySelector('[data-cm-track]');
      this.slides = this.track ? [...this.track.querySelectorAll('[data-cm-slide]')] : [];
      if (!this.slides.length) return;
      this.ready = true;

      this.controls = [...this.querySelectorAll('[data-cm-controls]')];
      this.prev = this.querySelector('[data-cm-prev]');
      this.next = this.querySelector('[data-cm-next]');
      this.bar = this.querySelector('[data-cm-bar]');
      this.thumb = this.querySelector('[data-cm-thumb]');
      this.status = this.querySelector('[data-cm-status]');
      this.classList.add('is-ready');

      this.frame = 0;
      this.width = 0;
      this.onScroll = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          this.update();
        });
      };
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      if (this.prev) this.prev.addEventListener('click', () => this.go(-1));
      if (this.next) this.next.addEventListener('click', () => this.go(1));
      this.addEventListener('keydown', (e) => this.onKey(e));
      // Theme-Editor: ausgewählten Block in den sichtbaren Bereich holen
      this.addEventListener('shopify:block:select', (e) => this.reveal(e.target));

      if ('ResizeObserver' in window) {
        this.ro = new ResizeObserver(() => {
          const w = this.track.clientWidth;
          if (w === this.width) return;
          this.width = w;
          this.update();
        });
        this.ro.observe(this.track);
      }
      this.width = this.track.clientWidth;
      this.update();
    }

    disconnectedCallback() {
      if (this.track && this.onScroll) this.track.removeEventListener('scroll', this.onScroll);
      if (this.ro) this.ro.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.announceTimer);
    }

    offsets() {
      const base = this.slides[0].offsetLeft;
      return this.slides.map((slide) => slide.offsetLeft - base);
    }

    maxScroll() {
      return Math.max(0, this.track.scrollWidth - this.track.clientWidth);
    }

    /* Index der Karte, die am linken Rand steht */
    index() {
      const x = this.track.scrollLeft;
      let best = 0;
      this.offsets().forEach((o, i, all) => {
        if (Math.abs(o - x) < Math.abs(all[best] - x)) best = i;
      });
      return best;
    }

    /* Ganz sichtbare Karten (mobil 1, Tablet 2, Desktop 4) = Schrittweite der Buttons */
    perView() {
      const offs = this.offsets();
      const width = this.slides[0].getBoundingClientRect().width;
      const pitch = offs[1] || width;
      const gap = Math.max(0, pitch - width);
      const view = this.track.clientWidth - (parseFloat(getComputedStyle(this.track).paddingLeft) || 0);
      return Math.max(1, Math.floor((view + gap + 1) / pitch));
    }

    scrollToIndex(i) {
      const offs = this.offsets();
      const target = Math.min(Math.max(i, 0), offs.length - 1);
      this.track.scrollTo({ left: Math.min(offs[target], this.maxScroll()), behavior: reduceMotion() ? 'auto' : 'smooth' });
    }

    go(dir) {
      const btn = dir < 0 ? this.prev : this.next;
      if (btn && btn.getAttribute('aria-disabled') === 'true') return;
      const max = this.maxScroll();
      const x = this.track.scrollLeft;
      if ((dir < 0 && x <= EDGE) || (dir > 0 && x >= max - EDGE)) return;
      this.scrollToIndex(this.index() + dir * this.perView());
      this.announceLater();
    }

    onKey(e) {
      if (e.altKey || e.ctrlKey || e.metaKey || e.shiftKey) return;
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.target.closest('input, textarea, select, [contenteditable="true"]')) return;
      e.preventDefault();
      this.go(e.key === 'ArrowLeft' ? -1 : 1);
    }

    reveal(target) {
      const slide = target && target.closest ? target.closest('[data-cm-slide]') : null;
      const i = this.slides.indexOf(slide);
      if (i >= 0) this.scrollToIndex(i);
    }

    setDisabled(btn, off) {
      if (btn) btn.setAttribute('aria-disabled', String(off));
    }

    update() {
      const max = this.maxScroll();
      const x = this.track.scrollLeft;
      const scrollable = max > EDGE;
      this.controls.forEach((el) => { el.hidden = !scrollable; });
      this.setDisabled(this.prev, !scrollable || x <= EDGE);
      this.setDisabled(this.next, !scrollable || x >= max - EDGE);
      if (scrollable && this.bar && this.thumb) {
        const barW = this.bar.clientWidth;
        const ratio = Math.min(1, this.track.clientWidth / this.track.scrollWidth);
        const thumbW = Math.max(24, Math.round(barW * ratio));
        const pos = max > 0 ? Math.min(1, Math.max(0, x / max)) : 0;
        this.thumb.style.width = `${thumbW}px`;
        this.thumb.style.transform = `translateX(${Math.round((barW - thumbW) * pos)}px)`;
      }
    }

    announceLater() {
      if (!this.status) return;
      clearTimeout(this.announceTimer);
      const run = () => {
        clearTimeout(this.announceTimer);
        this.track.removeEventListener('scrollend', run);
        this.announce();
      };
      this.track.addEventListener('scrollend', run, { once: true });
      this.announceTimer = setTimeout(run, reduceMotion() ? 60 : 900);
    }

    announce() {
      const n = this.slides.length;
      const first = this.index() + 1;
      const last = Math.min(n, first + this.perView() - 1);
      this.status.textContent = last > first ? `Posts ${first} bis ${last} von ${n}` : `Post ${first} von ${n}`;
    }
  }

  customElements.define('gcs-community-slider', GcsCommunitySlider);
})();
